import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Shield, Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../context/AdminAuthContext";

export default function AdminLogin() {
  const { user, login, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1B35] flex items-center justify-center">
        <Loader2 className="text-[#00B4D8] animate-spin" size={32} />
      </div>
    );
  }

  if (user) return <Navigate to="/admin/contactos" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setTimeout(() => toast.error("Completa correo y contraseña"), 0);
      return;
    }
    setSubmitting(true);
    try {
      await login(form.email.trim(), form.password);
      setSubmitting(false);
      setTimeout(() => toast.success("Bienvenido"), 0);
      navigate("/admin/contactos", { replace: true });
    } catch (err) {
      setSubmitting(false);
      const detail = err?.response?.data?.detail || "Credenciales inválidas";
      setTimeout(() => toast.error(typeof detail === "string" ? detail : "Error"), 0);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#061226] text-white flex items-center justify-center px-6 relative overflow-hidden"
    >
      <div className="absolute inset-0 gf-grid-bg opacity-30" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00B4D8]/40 to-transparent" />

      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <span className="w-11 h-11 flex items-center justify-center border-2 border-[#00B4D8]" style={{ borderRadius: "2px" }}>
            <Shield size={20} className="text-[#00B4D8]" strokeWidth={2.5} />
          </span>
          <div className="font-display font-black text-xl tracking-tight">
            <span className="text-white">GUARDA</span>
            <span className="text-slate-400"> FLEX</span>
            <sup className="ml-0.5 text-[10px] text-slate-400">™</sup>
          </div>
        </div>

        <div
          className="bg-[#0A1B35] border border-white/10 p-8 lg:p-10 relative"
          style={{ borderRadius: "2px" }}
        >
          <div className="absolute -top-px left-8 right-8 h-px bg-[#00B4D8]" />

          <div className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#00B4D8] mb-2">
            Panel administrativo
          </div>
          <h1 className="font-display font-black text-2xl tracking-tight text-white mb-8">
            Iniciar sesión
          </h1>

          <form onSubmit={onSubmit} noValidate className="space-y-5">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">
                Correo electrónico
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  data-testid="admin-login-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@guardaflex.com.mx"
                  className="w-full bg-[#061226] border border-white/15 text-white pl-10 pr-3 py-3 outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/30 transition-colors"
                  style={{ borderRadius: "2px" }}
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-white/70 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  data-testid="admin-login-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full bg-[#061226] border border-white/15 text-white pl-10 pr-3 py-3 outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/30 transition-colors"
                  style={{ borderRadius: "2px" }}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={submitting}
              className="gf-btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="inline-flex items-center gap-2">
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                <span>{submitting ? "Verificando" : "Entrar"}</span>
              </span>
            </button>
          </form>

          <p className="text-[11px] text-white/40 text-center mt-6">
            Acceso restringido. Solo personal autorizado de Guarda Flex.
          </p>
        </div>

        <button
          onClick={() => navigate("/")}
          className="mt-6 mx-auto block text-xs text-white/50 hover:text-[#00B4D8] transition-colors uppercase tracking-widest"
          data-testid="admin-back-home"
        >
          ← Volver al sitio público
        </button>
      </div>
    </div>
  );
}
